import { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Popover } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { LuChevronDown, LuChevronRight, LuLoader2, LuLogOut, LuUser } from 'react-icons/lu';
import { useLogout, useUser } from '../../../config/connection/auth';
import { DASHBOARD_ROUTE, LOGIN_ROUTE } from '../../../config';
import { SidebarContext } from '../../../providers/SidebarProvider';
import { useReportContext } from '../../../providers/ReportContext';
import { AuthUser } from '../../../features/auth';
import { Avatar } from '../../Elements/Avatar';
import { itemDaRota } from './SidebarData';

/** Paginas que ficam "dentro" de um item da barra e ganham um degrau no caminho. */
const SUBPAGINAS: Record<string, string> = {
    report: 'Relatório de Estoque Ideal',
    'report-rem': 'Relatório de Remarcação',
};

function Header() {
    const { isOpen } = useContext(SidebarContext);
    const { isGeneratingReport, resumoHeader } = useReportContext();
    const navigate = useNavigate();
    const location = useLocation();
    const logout = useLogout();
    const auth = useUser();

    const usuario = auth.data as AuthUser | undefined;
    const nome = usuario?.name || 'Usuário';

    const exit = async () => {
        await logout.mutateAsync({});
        navigate(LOGIN_ROUTE);
    }

    const item = itemDaRota(location.pathname);
    const subpagina = SUBPAGINAS[location.pathname.replace(/^\/app\//, '').split('/')[0]];

    return (
        <header className={`header${isOpen ? '' : ' header--recolhido'}`}>
            <div className={resumoHeader ? 'header-left header-left--resumo' : 'header-left'}>
                {/*
                  * Com o resumo rolado para fora da tela, os totais ocupam o
                  * lugar do caminho da pagina: e a informacao que o usuario precisa
                  * ter a mao enquanto percorre a tabela.
                  */}
                {resumoHeader
                    ? (
                        <div className="header-resumo">
                            {/* A remarcacao nao tem graficos de participacao; sem isto o
                              * botao subiria para abrir um balao vazio. */}
                            {resumoHeader.graficos && (
                                <Popover
                                    content={resumoHeader.graficos}
                                    trigger="click"
                                    placement="bottomLeft"
                                    overlayClassName="resumo-graficos-popover"
                                >
                                    <button type="button" className="resumo-graficos-trigger hoverable">
                                        <BarChartOutlined />
                                        Gráficos
                                    </button>
                                </Popover>
                            )}
                            {resumoHeader.totais.map(item => (
                                <div className="header-resumo-item" key={item.label}>
                                    <span className="header-resumo-label">{item.label}</span>
                                    <span className="header-resumo-valor">{item.valor}</span>
                                </div>
                            ))}
                        </div>
                    )
                    : (
                        <nav className="header-caminho" aria-label="Você está em">
                            {subpagina && item ? <>
                                <Link to={`/app/${item.link}`} className="header-caminho-link">{item.title}</Link>
                                <LuChevronRight className="header-caminho-seta" aria-hidden="true" />
                                <span className="header-caminho-atual" aria-current="page">{subpagina}</span>
                            </> : (
                                <span className="header-caminho-atual" aria-current="page">{item?.title ?? 'Realiza'}</span>
                            )}
                        </nav>
                    )}
            </div>

            <div className="header-direita">
                {/* Leva ao dashboard, que e onde o progresso do relatorio aparece. */}
                {isGeneratingReport && (
                    <button type="button" className="header-gerando" onClick={() => navigate(DASHBOARD_ROUTE)}>
                        <LuLoader2 className="header-gerando-icone" />
                        <span>Gerando relatório…</span>
                    </button>
                )}

                <Dropdown
                    trigger={['click']}
                    placement="bottomRight"
                    menu={{
                        items: [
                            {
                                key: 'conta',
                                type: 'group',
                                label: (
                                    <div className="header-conta">
                                        <strong>{nome}</strong>
                                        {usuario?.email && <span>{usuario.email}</span>}
                                    </div>
                                ),
                            },
                            { type: 'divider' },
                            { key: 'perfil', label: 'Meu perfil', icon: <LuUser /> },
                            { key: 'sair', label: 'Sair', icon: <LuLogOut />, danger: true },
                        ],
                        onClick: ({ key }) => key === 'sair' ? exit() : navigate('/app/profile'),
                    }}
                >
                    <button type="button" className="header-usuario" aria-label="Menu da conta">
                        <Avatar name={nome} size={32} />
                        <span className="header-usuario-nome">{nome}</span>
                        <LuChevronDown className="header-usuario-seta" />
                    </button>
                </Dropdown>
            </div>
        </header>
    )
}

export default Header;
